import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Pencil,
  Plus,
  Search,
  Trash2,
  ChevronDown,
  ChevronUp,
  XCircle,
  Package,
  Calendar,
} from "lucide-react";
import Modal from "./Modal";
import PackageModal from "./PackageModal";
import BookPackagePlanManager from "../../services/dashboard/PackageManager";
import toast from "react-hot-toast";

// --- Constants ---
const MEMBERSHIP_OPTIONS = [
  { value: "ONE_MONTH", label: "1 Month" },
  { value: "THREE_MONTHS", label: "3 Months" },
  { value: "SIX_MONTHS", label: "6 Months" },
  { value: "ONE_YEAR", label: "1 Year" },
];

const PACKAGE_OPTIONS = [
  { value: "ONE_BOOK", label: "1 Book" },
  { value: "TWO_BOOKS", label: "2 Books" },
  { value: "THREE_BOOKS", label: "3 Books" },
];

// --- Reusable Empty Package Structure ---
const EMPTY_PACKAGE_FORM = {
  packageId: null,
  packageType: "",
  numberOfBooks: "",
  packageName: "",
  highlightLabel: "",
  price: "",
  packDescription: "",
  oneTimeRegistration: "",
  refundDeposit: "",
  bookPackagePlanId: null,
};

export default function PlanPackageManager() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedPlans, setExpandedPlans] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Unified Plan+Packages modal state
  const [showModal, setShowModal] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [form, setForm] = useState({
    planId: null,
    membershipPlan: "",
    validityDays: "",
    packDescription: "",
    planName: "",
    planTagline: "",
    packages: [{ ...EMPTY_PACKAGE_FORM }],
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    message: "",
    onConfirm: null,
    confirmText: "Yes, Confirm",
  });

  const askConfirmation = ({
    message,
    onConfirm,
    confirmText = "Yes, Confirm",
  }) => {
    setConfirmModal({ show: true, message, onConfirm, confirmText });
  };

  // --- Data Fetching ---
  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await BookPackagePlanManager.getAllBookPackagePlans();
      setPlans(Array.isArray(resp) ? resp : []);
    } catch (err) {
      toast.error("Failed to load plans. Please try again.");
      console.error("Error fetching plans:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // --- Search and Filter ---
  const filteredPlans = useMemo(
    () =>
      plans.filter((p) =>
        (
          MEMBERSHIP_OPTIONS.find((o) => o.value === p.membershipPlan)?.label ||
          p.membershipPlan
        )
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ),
    [plans, searchTerm]
  );

  // --- UI Interactions ---
  const toggleExpand = (id) =>
    setExpandedPlans((prev) => ({ ...prev, [id]: !prev[id] }));

  const openModal = (plan = null) => {
    if (plan) {
      setForm({
        planId: plan.planId,
        membershipPlan: plan.membershipPlan,
        validityDays: plan.validityDays,
        packDescription: plan.packDescription,
        planName: plan.planName || "",
        planTagline: plan.planTagline || "",
        packages: plan.packages.length
          ? plan.packages.map((pkg) => ({
              packageId: pkg.packageId,
              packageType: pkg.packageType,
              numberOfBooks: pkg.numberOfBooks || "",
              price: pkg.price,
              packDescription: pkg.packDescription,
              oneTimeRegistration: pkg.oneTimeRegistration,
              refundDeposit: pkg.refundDeposit,
              packageName: pkg.packageName || "",
              highlightLabel: pkg.highlightLabel || "",
              bookPackagePlanId: plan.planId,
            }))
          : [{ ...EMPTY_PACKAGE_FORM }],
      });
    } else {
      setForm({
        planId: null,
        membershipPlan: "",
        validityDays: "",
        packDescription: "",
        planName: "",
        planTagline: "",
        packages: [{ ...EMPTY_PACKAGE_FORM }],
      });
    }
    setActivePlan(plan);
    setShowModal(true);
  };

  // --- Form Handling ---
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addPackageRow = () =>
    setForm((prev) => ({
      ...prev,
      packages: [...prev.packages, { ...EMPTY_PACKAGE_FORM }],
    }));

  const removePackageRow = (idx) =>
    setForm((prev) => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== idx),
    }));

  const confirmRemovePackage = (idx) => {
    askConfirmation({
      message: `Remove package #${idx + 1}? You'll lose its settings.`,
      confirmText: "Yes, Remove",
      onConfirm: () => {
        setConfirmModal((m) => ({ ...m, show: false }));
        removePackageRow(idx);
      },
    });
  };

  const updatePackageField = (idx, field, value) =>
    setForm((prev) => {
      const updatedPackages = [...prev.packages];
      updatedPackages[idx] = { ...updatedPackages[idx], [field]: value };
      return { ...prev, packages: updatedPackages };
    });

  // --- API Actions ---
  const savePlan = async () => {
    setIsSaving(true);
    const payload = {
      planId: form.planId,
      membershipPlan: form.membershipPlan,
      validityDays: parseInt(form.validityDays, 10),
      packDescription: form.packDescription,
      planName: form.planName,
      planTagline: form.planTagline,
      packages: form.packages.map((pkg) => ({
        packageId: pkg.packageId,
        packageType: pkg.packageType,
        numberOfBooks: parseInt(pkg.numberOfBooks, 10),
        price: parseFloat(pkg.price),
        packDescription: pkg.packDescription,
        oneTimeRegistration: parseFloat(pkg.oneTimeRegistration),
        refundDeposit: parseFloat(pkg.refundDeposit),
        packageName: pkg.packageName,
        highlightLabel: pkg.highlightLabel,
        bookPackagePlanId: form.planId,
      })),
    };

    try {
      if (form.planId) {
        await BookPackagePlanManager.updateBookPackagePlan(
          form.planId,
          payload
        );
        toast.success("Plan updated successfully!");
      } else {
        await BookPackagePlanManager.addBookPackagePlan(payload);
        toast.success("Plan created successfully!");
      }
      fetchPlans();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data.responseMessage);
      } else {
        toast.error("Error saving plan. Please check your inputs.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const confirmSavePlan = () => {
    askConfirmation({
      message: activePlan
        ? "Are you sure you want to apply these changes to the plan?"
        : "Create this new plan and packages?",
      confirmText: activePlan ? "Yes, Update" : "Yes, Create",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, show: false }));
        await savePlan();
        setShowModal(false);
      },
    });
  };

  const confirmDeletePlan = (planId) => {
    askConfirmation({
      message:
        "Are you sure you want to delete this plan and all its packages? This cannot be undone.",
      confirmText: "Yes, Delete",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, show: false }));
        try {
          await BookPackagePlanManager.deleteBookPackagePlan(planId);
          toast.success("Plan deleted successfully.");
          fetchPlans();
        } catch (err) {
          if (err.response?.status === 409) {
            toast.error(err.response.data.responseMessage);
          } else {
            toast.error("Failed to delete plan. Please try again.");
          }
        }
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          ← Back
        </button>

        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Membership Plans & Book Packages
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Configure subscription plans and pricing
            </p>
          </div>

          {/* Search + Add Button */}
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:shadow-lg transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={() => openModal()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors shadow-sm whitespace-nowrap w-full sm:w-auto justify-center"
            >
              <Plus className="w-4 h-4" /> Add New Plan
            </button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              Loading plans...
            </p>
          </div>
        ) : (
          <>
            {/* No Plans Found State */}
            {filteredPlans.length === 0 && (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-600 dark:text-gray-400">
                  {searchTerm
                    ? `No plans found matching "${searchTerm}".`
                    : "No membership plans available yet. Click 'Add New Plan' to get started!"}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => openModal()}
                    className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg shadow-sm transition-colors"
                  >
                    <Plus className="w-5 h-5" /> Create First Plan
                  </button>
                )}
              </div>
            )}

            {/* Elite Premium Plan Cards - Final Polish */}
            <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
              {filteredPlans.map((plan) => {
                const label =
                  MEMBERSHIP_OPTIONS.find((o) => o.value === plan.membershipPlan)
                    ?.label || plan.membershipPlan;
                const isOpen = expandedPlans[plan.planId];
                return (
                  <div
                    key={plan.planId}
                    className="group bg-white dark:bg-gray-800 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.08)] border border-gray-200/40 dark:border-gray-700/40 hover:border-gray-300/60 dark:hover:border-gray-600/60 overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
                  >
                    {/* Premium Card Header */}
                    <div className="p-4 border-b border-gray-100/80 dark:border-gray-700/40 flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-0.5 h-5 bg-orange-500 rounded-full"></div>
                          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                            {plan.planName || label}
                          </h2>
                        </div>
                        {plan.planTagline && (
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 ml-2.5 leading-relaxed">
                            {plan.planTagline}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200">
                        <button
                          onClick={() => openModal(plan)}
                          title="Edit Plan"
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-150 hover:scale-105"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => confirmDeletePlan(plan.planId)}
                          title="Delete Plan"
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-150 hover:scale-105"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Card Body - Refined Layout */}
                    <div className="p-4 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                          <Calendar className="w-3.5 h-3.5 opacity-70" />
                          <span className="text-xs font-medium">{plan.validityDays} days</span>
                        </span>
                        <button
                          onClick={() => toggleExpand(plan.planId)}
                          className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-all duration-200 text-xs font-medium group/btn"
                        >
                          Packages ({plan.packages.length})
                          <ChevronDown 
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      </div>

                      {plan.packDescription && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                          {plan.packDescription}
                        </p>
                      )}

                      {/* Packages Section - Elite Structured Rows */}
                      {isOpen && (
                        <div 
                          className="mt-3 pt-3 space-y-2 animate-in fade-in slide-in-from-top-2 duration-200"
                          style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}
                        >
                          {plan.packages.length === 0 ? (
                            <div className="text-center py-6">
                              <Package className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2 opacity-50" />
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                No packages added yet
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {plan.packages.map((pkg) => (
                                <div
                                  key={pkg.packageId}
                                  className="flex items-center justify-between p-2.5 bg-gray-50/50 dark:bg-gray-900/30 rounded-lg border border-gray-100/80 dark:border-gray-700/40 hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-150 group/pkg"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {pkg.packageName ||
                                          PACKAGE_OPTIONS.find(
                                            (o) => o.value === pkg.packageType
                                          )?.label}
                                      </span>
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-gray-100/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 border border-gray-200/50 dark:border-gray-700/50">
                                        {PACKAGE_OPTIONS.find(
                                          (o) => o.value === pkg.packageType
                                        )?.label}
                                      </span>
                                      {pkg.highlightLabel && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-orange-50/80 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-200/50 dark:border-orange-800/50 transition-opacity duration-150 group-hover/pkg:opacity-90">
                                          {pkg.highlightLabel}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                      Borrow {pkg.numberOfBooks} at a time
                                    </p>
                                  </div>
                                  <div className="text-right ml-3 flex-shrink-0">
                                    <div className="text-[15px] font-semibold text-gray-900 dark:text-white">
                                      ₹{pkg.price}
                                    </div>
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400 font-normal">
                                      ₹{pkg.refundDeposit} deposit
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Elite Premium Stripe-Style Modal */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={
          activePlan ? "Edit Plan & Packages" : "Create New Plan & Packages"
        }
        description="Configure membership details and package options"
      >
        <div className="space-y-8 text-gray-800 dark:text-gray-200">
          {/* Section 1: Plan Info - Guided Structure */}
          <div className="space-y-4">
            <div className="pb-3 mb-1" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <h3 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Plan Information
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Plan Name */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Plan Name <span className="text-red-400 text-[10px]">*</span>
                </label>
                <input
                  type="text"
                  name="planName"
                  value={form.planName}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-gray-50/30 dark:bg-gray-700/30 dark:text-gray-100 transition-all duration-150 hover:bg-white dark:hover:bg-gray-700"
                  placeholder="e.g., Sprout Plan"
                  required
                />
              </div>

              {/* Membership Duration */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Duration <span className="text-red-400 text-[10px]">*</span>
                </label>
                <select
                  name="membershipPlan"
                  value={form.membershipPlan}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-gray-50/30 dark:bg-gray-700/30 dark:text-gray-100 transition-all duration-150 hover:bg-white dark:hover:bg-gray-700"
                  required
                >
                  <option value="" disabled>
                    Select duration...
                  </option>
                  {MEMBERSHIP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Plan Tagline */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Tagline
                </label>
                <input
                  type="text"
                  name="planTagline"
                  value={form.planTagline}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-gray-50/30 dark:bg-gray-700/30 dark:text-gray-100 transition-all duration-150 hover:bg-white dark:hover:bg-gray-700"
                  placeholder="e.g., Perfect for beginners"
                />
              </div>

              {/* Validity Days */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Validity (days) <span className="text-red-400 text-[10px]">*</span>
                </label>
                <input
                  name="validityDays"
                  type="number"
                  value={form.validityDays}
                  onChange={handleFormChange}
                  placeholder="e.g., 30"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-gray-50/30 dark:bg-gray-700/30 dark:text-gray-100 transition-all duration-150 hover:bg-white dark:hover:bg-gray-700"
                  required
                  min="1"
                />
              </div>

              {/* Plan Description */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Description
                </label>
                <textarea
                  name="packDescription"
                  rows={2}
                  value={form.packDescription}
                  onChange={handleFormChange}
                  placeholder="Brief description of this plan..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-gray-50/30 dark:bg-gray-700/30 dark:text-gray-100 transition-all duration-150 hover:bg-white dark:hover:bg-gray-700 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Packages - Guided Structure */}
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 mb-1" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <h3 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <Package className="w-3.5 h-3.5" />
                Packages
              </h3>
              <button
                onClick={addPackageRow}
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-colors duration-150"
              >
                <Plus className="w-3.5 h-3.5" /> Add Package
              </button>
            </div>

            <div className="space-y-3 max-h-[440px] overflow-y-auto pr-2 elite-scrollbar">
              <style>{`
                .elite-scrollbar::-webkit-scrollbar {
                  width: 5px;
                }
                .elite-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(203 213 225 / 0.6);
                  border-radius: 3px;
                  transition: background-color 0.15s;
                }
                .dark .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(71 85 105 / 0.6);
                }
                .elite-scrollbar::-webkit-scrollbar-thumb:hover {
                  background-color: rgb(148 163 184);
                }
                .dark .elite-scrollbar::-webkit-scrollbar-thumb:hover {
                  background-color: rgb(100 116 139);
                }
              `}</style>
              {form.packages.map((pkg, idx) => (
                <div
                  key={idx}
                  className="group/package relative p-3 bg-gray-50/40 dark:bg-gray-900/30 rounded-lg border border-gray-200/60 dark:border-gray-700/50 transition-all duration-150 hover:border-gray-300 dark:hover:border-gray-600"
                >
                  {/* Package Header - Compact */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {pkg.packageName || `Package ${idx + 1}`}
                    </span>
                    {form.packages.length > 1 && (
                      <button
                        onClick={() => confirmRemovePackage(idx)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-0 group-hover/package:opacity-100 transition-all duration-150"
                        title="Remove Package"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Compact 2-Column Grid Layout */}
                  <div className="space-y-3">
                    {/* Row 1: Type | Books */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Type <span className="text-red-400">*</span>
                        </label>
                        <select
                          value={pkg.packageType}
                          onChange={(e) =>
                            updatePackageField(idx, "packageType", e.target.value)
                          }
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-700 dark:text-gray-100 transition-all duration-150"
                          required
                        >
                          <option value="" disabled>
                            Select...
                          </option>
                          {PACKAGE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Books <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="number"
                          value={pkg.numberOfBooks}
                          onChange={(e) =>
                            updatePackageField(idx, "numberOfBooks", e.target.value)
                          }
                          placeholder="1"
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-700 dark:text-gray-100 transition-all duration-150"
                          required
                          min="1"
                        />
                      </div>
                    </div>

                    {/* Row 2: Package Name (full width) */}
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Package Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={pkg.packageName}
                        onChange={(e) =>
                          updatePackageField(idx, "packageName", e.target.value)
                        }
                        placeholder="e.g., Sprout Lite"
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-700 dark:text-gray-100 transition-all duration-150"
                        required
                      />
                    </div>

                    {/* Row 3: Price | Registration */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Price (₹) <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="number"
                          value={pkg.price}
                          onChange={(e) =>
                            updatePackageField(idx, "price", e.target.value)
                          }
                          placeholder="499"
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-700 dark:text-gray-100 transition-all duration-150"
                          required
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Registration (₹) <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="number"
                          value={pkg.oneTimeRegistration}
                          onChange={(e) =>
                            updatePackageField(
                              idx,
                              "oneTimeRegistration",
                              e.target.value
                            )
                          }
                          placeholder="150"
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-700 dark:text-gray-100 transition-all duration-150"
                          required
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Row 4: Deposit | Highlight Label */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Deposit (₹) <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="number"
                          value={pkg.refundDeposit}
                          onChange={(e) =>
                            updatePackageField(idx, "refundDeposit", e.target.value)
                          }
                          placeholder="500"
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-700 dark:text-gray-100 transition-all duration-150"
                          required
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Highlight Label
                        </label>
                        <input
                          type="text"
                          value={pkg.highlightLabel}
                          onChange={(e) =>
                            updatePackageField(idx, "highlightLabel", e.target.value)
                          }
                          placeholder="e.g., Best Value"
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-700 dark:text-gray-100 transition-all duration-150"
                        />
                      </div>
                    </div>

                    {/* Row 5: Description (full width) */}
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Description
                      </label>
                      <textarea
                        rows={2}
                        value={pkg.packDescription}
                        onChange={(e) =>
                          updatePackageField(idx, "packDescription", e.target.value)
                        }
                        placeholder="Package details..."
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-700 dark:text-gray-100 transition-all duration-150 resize-none"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sticky Footer - Premium Polish */}
          <div className="flex justify-end gap-3 pt-5 sticky bottom-0 bg-white/98 dark:bg-gray-800/98 backdrop-blur-sm -mx-6 px-6 -mb-6 pb-5" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <button
              onClick={() => setShowModal(false)}
              type="button"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-all duration-150"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={confirmSavePlan}
              type="submit"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 hover:bg-orange-700 text-white shadow-sm hover:shadow-md transition-all duration-150"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  {form.planId ? "Update Plan" : "Create Plan"}
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <PackageModal
        show={confirmModal.show}
        onClose={() => setConfirmModal((m) => ({ ...m, show: false }))}
        title="Please Confirm"
      >
        <p className="text-center text-gray-700 dark:text-gray-300 mb-6">
          {confirmModal.message}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setConfirmModal((m) => ({ ...m, show: false }))}
            className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirmModal.onConfirm}
            className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            {confirmModal.confirmText}
          </button>
        </div>
      </PackageModal>
    </div>
  );
}
